import { useState } from 'react';
import { toast } from '../components/Toast';
import { crmApi } from '../services/crmApi';
import { downloadBlob } from '../utils/downloadBlob';
import type { CrmApplication, LoanCompletenessReport } from '../types/crm';

interface LoanDeps {
  id: string | undefined;
  application: CrmApplication | null;
  loadApplication: () => Promise<void>;
}

export function useApplicationLoan({ id, application, loadApplication }: LoanDeps) {
  const [generatingLoanBusinessPlan, setGeneratingLoanBusinessPlan] = useState(false);
  const [generatingLoanCostEstimate, setGeneratingLoanCostEstimate] = useState(false);
  const [generatingRepaymentSchedule, setGeneratingRepaymentSchedule] = useState(false);
  const [generatingOperatorApp, setGeneratingOperatorApp] = useState(false);
  const [generatingLoanContract, setGeneratingLoanContract] = useState(false);
  const [loanCompletenessReport, setLoanCompletenessReport] = useState<LoanCompletenessReport | null>(null);
  const [showLoanCompleteness, setShowLoanCompleteness] = useState(false);
  const [checkingLoanCompleteness, setCheckingLoanCompleteness] = useState(false);

  const handleGenerateLoanBusinessPlan = async () => {
    if (!id || !application) return;
    setGeneratingLoanBusinessPlan(true);
    try {
      const blob = await crmApi.generateLoanBusinessPlan(parseInt(id));
      downloadBlob(blob, `Biznesplan_${application.first_name}_${application.last_name}.txt`);
    } catch (error) {
      console.error('Error generating loan business plan:', error);
      toast.error('Błąd podczas generowania biznesplanu');
    } finally {
      setGeneratingLoanBusinessPlan(false);
    }
  };

  const handleGenerateLoanCostEstimate = async () => {
    if (!id || !application) return;
    setGeneratingLoanCostEstimate(true);
    try {
      const blob = await crmApi.generateLoanCostEstimate(parseInt(id));
      downloadBlob(blob, `Kosztorys_${application.first_name}_${application.last_name}.txt`);
    } catch (error) {
      console.error('Error generating cost estimate:', error);
      toast.error('Błąd podczas generowania kosztorysu');
    } finally {
      setGeneratingLoanCostEstimate(false);
    }
  };

  const handleGenerateRepaymentSchedule = async () => {
    if (!id || !application) return;
    setGeneratingRepaymentSchedule(true);
    try {
      const blob = await crmApi.generateRepaymentSchedule(parseInt(id));
      downloadBlob(blob, `Harmonogram_splat_${application.first_name}_${application.last_name}.txt`);
    } catch (error) {
      console.error('Error generating repayment schedule:', error);
      toast.error('Błąd podczas generowania harmonogramu spłat');
    } finally {
      setGeneratingRepaymentSchedule(false);
    }
  };

  const handleGenerateOperatorApplication = async () => {
    if (!id || !application) return;
    setGeneratingOperatorApp(true);
    try {
      const blob = await crmApi.generateOperatorApplication(parseInt(id));
      downloadBlob(blob, `Wniosek_operatora_${application.first_name}_${application.last_name}.txt`);
    } catch (error) {
      console.error('Error generating operator application:', error);
      toast.error('Błąd podczas generowania wniosku do operatora');
    } finally {
      setGeneratingOperatorApp(false);
    }
  };

  const handleLoanCompletenessCheck = async () => {
    if (!id) return;
    setCheckingLoanCompleteness(true);
    try {
      const report = await crmApi.loanCompletenessCheck(parseInt(id));
      setLoanCompletenessReport(report);
      setShowLoanCompleteness(true);
    } catch (error) {
      console.error('Error checking loan completeness:', error);
      toast.error('Błąd podczas sprawdźania kompletności');
    } finally {
      setCheckingLoanCompleteness(false);
    }
  };

  const handleGenerateLoanContract = async () => {
    if (!id || !application) return;
    setGeneratingLoanContract(true);
    try {
      const blob = await crmApi.generateLoanContract(parseInt(id));
      downloadBlob(blob, `Umowa_pozyczka_${application.first_name}_${application.last_name}.txt`);
    } catch (error) {
      console.error('Error generating loan contract:', error);
      toast.error('Błąd podczas generowania umowy pożyczkowej');
    } finally {
      setGeneratingLoanContract(false);
    }
  };

  const handleLoanStatusChange = async (newLoanStatus: string) => {
    if (!id) return;
    try {
      await crmApi.updateLoanStatus(parseInt(id), newLoanStatus);
      await loadApplication();
    } catch (error) {
      console.error('Error updating loan status:', error);
      toast.error('Błąd podczas zmiany statusu pożyczki');
    }
  };

  return {
    generatingLoanBusinessPlan, generatingLoanCostEstimate, generatingRepaymentSchedule,
    generatingOperatorApp, generatingLoanContract,
    loanCompletenessReport, showLoanCompleteness, setShowLoanCompleteness, checkingLoanCompleteness,
    handleGenerateLoanBusinessPlan, handleGenerateLoanCostEstimate,
    handleGenerateRepaymentSchedule, handleGenerateOperatorApplication,
    handleLoanCompletenessCheck, handleGenerateLoanContract, handleLoanStatusChange,
  };
}
